import { PaymentMethodsEnum } from '@ctct/domain-layer';
import type { BillingAccount, BillingInfo } from '@ctct/domain-layer';
import type { ApplePayBillingAddress, ApplePayPaymentData } from '@ctct/infrastructure-layer';
import { envs, SECURE_PAY_CLIENT_ID, SECURE_PAY_EVENTS, SecurePay } from '@ctct/infrastructure-layer';
import { observabilityAppService } from '@ctct/myaccount-utils-static';
import type { PaymentMethodsPageStore } from '@ctct/state-client-layer';

import type { TranslationGetter } from '../../../types';
import {
  getPaymentLogoName,
  paymentMethodsEnum,
  transformCreditCardName,
} from '../../_shared/PaymentMethods/utils/transformCreditCardName';
import type { BillingInfoFormValues } from '../../BillingInfoFormManager';
import { paymentMethodsEventBus } from '../PaymentMethodsEventBus';

const APPLE_PAY_TOKEN_TIMEOUT_TIME = 3540000; // 3540000ms = 59 min
// property "payment.tokenization.expiration" in paas is set to 60 minutes
// we set timeout on FE on a slightly shorter time (59 minutes)

export const APPLEPAY_VALUES_AVAILABLE_FOR_BILLING_INFO_FORM: (keyof BillingInfoFormValues)[] = [
  'firstname',
  'lastname',
  'address1',
  'city',
  'country',
  'phone',
  'state',
  'zipcode',
];

export class ApplePayService {
  private securePayInstance: SecurePay | null = null;

  constructor(
    private pageStore: PaymentMethodsPageStore,
    private billingInfo: BillingInfo,
    private billingAccount: BillingAccount,
    private t: TranslationGetter
  ) {}

  public api() {
    return {
      setApplePayTotalChanged: ({ amount, taxAmount, currency }) => {
        const { applePayTotal: currentTotal } = this.pageStore.getState();

        if (!currentTotal) {
          return;
        }

        if (
          Number(currentTotal.amount) !== Number(amount) ||
          Number(currentTotal.taxAmount) !== Number(taxAmount) ||
          currentTotal.currency !== currency
        ) {
          this.pageStore.setState({
            applePayTotalChanged: true,
            applePayTotal: null,
            applePayConnected: false,
            applePayCreditCardData: null,
            applePayToken: null,
          });
        }
      },

      setApplePayShouldGetAddressWhenLoaded: (shouldGetAddress: boolean) => {
        const isApplePayLoaded = this.pageStore.getState().applePayAddedCssToIframe;

        if (isApplePayLoaded) {
          this.handlers().setShouldGetAddress(shouldGetAddress);
        }

        this.pageStore.setState({
          applePayShouldGetAddressWhenLoaded: shouldGetAddress,
        });
      },

      setApplePayShouldGetTaxUpdateWhenLoaded: (shouldGetTaxUpdate: boolean) => {
        const isApplePayLoaded = this.pageStore.getState().applePayAddedCssToIframe;

        if (isApplePayLoaded) {
          this.handlers().setShouldGetTaxUpdate(shouldGetTaxUpdate);
        }

        this.pageStore.setState({
          applePayShouldGetTaxUpdateWhenLoaded: shouldGetTaxUpdate,
        });
      },

      setApplePayPaymentData: (paymentData: Omit<ApplePayPaymentData, 'label'>) => {
        this.handlers().setApplePayPaymentData(paymentData);
      },

      setShouldGetAddress: (shouldGetAddress: boolean) => {
        this.handlers().setShouldGetAddress(shouldGetAddress);
      },

      setShouldGetTaxUpdate: (shouldGetTaxUpdate: boolean) => {
        this.handlers().setShouldGetTaxUpdate(shouldGetTaxUpdate);
      },

      setApplePayTaxValue: (taxValue: number | null) => {
        const isApplePayLoaded = this.pageStore.getState().applePayAddedCssToIframe;

        if (isApplePayLoaded) {
          this.handlers().setApplePayTaxValue(taxValue);
        }
      },

      getHasNewApplePayToken: () => {
        return Boolean(this.pageStore.getState().applePayToken);
      },

      getIsApplePayLoaded: () => {
        return this.pageStore.getState().applePayAddedCssToIframe;
      },

      getPaymentLabel: () => {
        return this.getPaymetLabel();
      },
    };
  }

  public data() {
    return {
      getApplePaySecurePayIframeId: () => SECURE_PAY_EVENTS.APPLE_PAY_SECUREPAY_ID,

      getApplePayURL: (shouldGetAddress = false, shouldGetTaxUpdate = false) => {
        const addr = new URL(`https://${envs.getSecurePayEnv()}securepay.ctct.io/payment/applepay.html`);

        addr.searchParams.append('secureDomain', 'true');

        addr.searchParams.append('clientId', SECURE_PAY_CLIENT_ID);

        // if shouldGetAddress set to true, securepay apple pay iframe will send event with billing address data
        if (shouldGetAddress) {
          addr.searchParams.append('getAddress', 'true');
        }

        // if shouldGetTaxUpdate set to true, securepay apple pay iframe will send event with address data, and wait for "setTaxValue" event with value of the tax calulated based on location
        if (shouldGetTaxUpdate) {
          addr.searchParams.append('getTaxUpdate', 'true');
        }

        return addr.toString();
      },

      getApplePayCreditCardData: () => {
        const newApplePayCreditCardData = this.pageStore.getState().applePayCreditCardData;
        const applePayToken = this.data().getApplePayToken();

        if (newApplePayCreditCardData && applePayToken) {
          return {
            card_token: applePayToken,
            credit_card_type: getPaymentLogoName(newApplePayCreditCardData.cardType),
            expiration_date: this.transformCreditCardExpirationDate(newApplePayCreditCardData.cardExpiration),
            masked_credit_card_number: this.chunkCreditCardNumber(
              newApplePayCreditCardData.cardNumber,
              newApplePayCreditCardData.cardType
            ),
            digital_wallet: PaymentMethodsEnum.APPLE_PAY,
          };
        }

        // if Apple Pay is current payment method, data is stored as a credit card
        if (this.billingInfo.getDigitalWalletInfo() === PaymentMethodsEnum.APPLE_PAY) {
          return this.billingInfo.getCreditCardInfo();
        }

        return null;
      },

      getIsApplePaySupportedInBrowser: () => {
        return this.getApplePaySecurePayInstance().isApplePaySupportedInBrowser();
      },

      getApplePayToken: () => {
        const newApplePayToken = this.pageStore.getState().applePayToken;

        if (newApplePayToken) {
          return newApplePayToken;
        }

        const existingApplePayToken = this.billingInfo.getApplePayCreditCardToken();

        if (existingApplePayToken) {
          return existingApplePayToken;
        }

        throw new Error('Apple Pay Token is missing. Please make sure that you click Apple Pay button.');
      },

      getApplePayCreditCardName: () => {
        const cardType =
          this.pageStore.getState().applePayCreditCardData?.cardType || this.billingInfo.getCreditCardType();

        return transformCreditCardName(cardType);
      },

      getApplePayCreditCardExpirationDate: () => {
        const expirationDate =
          this.pageStore.getState().applePayCreditCardData?.cardExpiration || this.billingInfo.getExpirationDate();

        return this.transformCreditCardExpirationDate(expirationDate);
      },

      getApplePayCreditCardNumberFormatted: () => {
        const cardNumber =
          this.pageStore.getState().applePayCreditCardData?.cardNumber ||
          this.billingInfo.getMaskedCreditCardNumber() ||
          '';

        const cardType =
          this.pageStore.getState().applePayCreditCardData?.cardType || this.billingInfo.getCreditCardType() || '';

        return this.chunkCreditCardNumber(cardNumber, cardType);
      },
    };
  }

  public handlers() {
    return {
      onStartListeningApplePaySecurePay: () => this.getApplePaySecurePayInstance().startListening(),
      onStopListeningApplePaySecurePay: () => {
        this.getApplePaySecurePayInstance().stopListening();
        this.clearTimeoutIds();
      },
      onApplePaySecurePayIframeError: (event: React.SyntheticEvent<HTMLIFrameElement, Event>) => {
        observabilityAppService.notify(new Error('ApplePay SecurePay iframe failed to load'), event);
      },
      onApplePaySecurePayIframeLoad: (e) => {
        const filename = 'ctct/cbs_ftpf_securepay_applepay.css';

        this.getApplePaySecurePayInstance().injectCss(e, filename);
        this.addTimeoutId();
      },

      setApplePayPaymentData: (paymentData: Omit<ApplePayPaymentData, 'label'>) => {
        const isPaymentAmountGraterThanZero = Number(paymentData.amount) > 0;

        if (isPaymentAmountGraterThanZero) {
          this.getApplePaySecurePayInstance().setApplePayPaymentData({ ...paymentData, label: this.getPaymetLabel() });
        }

        // reset existing store values
        this.pageStore.setState({
          applePayTotal: null,
          applePayConnected: false,
          applePayCreditCardData: null,
          applePayToken: null,
          applePayZeroValuePayment: isPaymentAmountGraterThanZero ? null : { currency: paymentData.currency },
        });
      },

      setApplePayTaxValue: (taxValue: number | null) => {
        this.getApplePaySecurePayInstance().setApplePayTaxValue(taxValue);
      },

      setShouldGetAddress: (shouldGetAddress: boolean) => {
        this.getApplePaySecurePayInstance().setShouldGetAddress(shouldGetAddress);
      },

      setShouldGetTaxUpdate: (shouldGetTaxUpdate: boolean) => {
        this.getApplePaySecurePayInstance().setShouldGetTaxUpdate(shouldGetTaxUpdate);
      },

      setApplePayIsLoaded: (isReady: boolean) => {
        this.pageStore.setState({
          applePayAddedCssToIframe: isReady,
        });
      },

      onApplePayEditClick: () => {
        this.clearTokenTimeout();

        this.pageStore.setState({
          applePayConnected: false,
          applePayCreditCardData: null,
          applePayToken: null,
          applePayTotal: null,
          applePayAddedCssToIframe: false,
          creditCardConnected: false,
        });
      },
    };
  }

  private timeoutIds: number[] = [];

  private tokenTimeoutId: null | number = null;

  private addTimeoutId() {
    this.timeoutIds.push(
      setTimeout(() => {
        const cantReceiveEventsFromIframe = !this.pageStore.getState().applePayAddedCssToIframe;

        if (cantReceiveEventsFromIframe) {
          this.pageStore.setState({ applePaySecurePayIframeBlockedMessage: true });
        }
      }, 5000) as unknown as number
    );
  }

  private clearTimeoutIds() {
    this.timeoutIds.forEach(clearTimeout);
    this.timeoutIds = [];
  }

  private getApplePaySecurePayInstance() {
    if (!this.securePayInstance) {
      this.securePayInstance = new SecurePay(this.applePaySecurePayCallback.bind(this));
    }

    return this.securePayInstance;
  }

  private mapApplePayBillingAddressToBillingInfoFormValues(
    applePayAddress: ApplePayBillingAddress
  ): Partial<BillingInfoFormValues> {
    const { givenName, familyName, addressLines, locality, administrativeArea, countryCode, postalCode, phoneNumber } =
      applePayAddress;
    const [address1, address2] = addressLines;

    return {
      ...(givenName && { firstname: givenName }),
      ...(familyName && { lastname: familyName }),
      ...(address1 && { address1 }),
      ...(address2 && { address2 }),
      ...(locality && { city: locality }),
      ...(administrativeArea && { state: administrativeArea }),
      ...(countryCode && { country: countryCode }),
      ...(postalCode && { zipcode: postalCode }),
      ...(phoneNumber && { phone: phoneNumber }),
    };
  }

  private applePaySecurePayCallback(eventType, data) {
    switch (eventType) {
      case SECURE_PAY_EVENTS.CSS_ADDED_EVENT:
        this.api().setShouldGetAddress(this.pageStore.getState().applePayShouldGetAddressWhenLoaded);
        this.api().setShouldGetTaxUpdate(this.pageStore.getState().applePayShouldGetTaxUpdateWhenLoaded);
        this.pageStore.setState({ applePayAddedCssToIframe: true });

        paymentMethodsEventBus.emit('applePayIFrameLoaded', { source: 'onMount' });

        this.clearTokenTimeout();

        break;

      case SECURE_PAY_EVENTS.APPLE_PAY_TOKEN_EVENT:
        this.pageStore.setState({
          selectedMethod: PaymentMethodsEnum.APPLE_PAY,
          applePayConnected: true,
          applePayTokenExpired: false,
          applePayTotalChanged: false,
          applePayAddedCssToIframe: false,
          applePayCreditCardData: data.token.creditCard,
          applePayToken: data.token.id,
          applePayTotal: data.paymentData,
          paymentMethodCanBeUpdated: true,
        });

        this.startTokenTimeout();

        paymentMethodsEventBus.emit('applePayConnected', { source: 'asyncResponse' });
        paymentMethodsEventBus.emit('paymentMethodCanBeUpdated', true, { source: 'asyncResponse' });

        break;
      case SECURE_PAY_EVENTS.APPLE_PAY_ADDRESS_EVENT:
        paymentMethodsEventBus.emit(
          'partialBillingInfoFormDataFromApplePay',
          this.mapApplePayBillingAddressToBillingInfoFormValues(data as ApplePayBillingAddress),
          { source: 'asyncResponse' }
        );

        break;
      case SECURE_PAY_EVENTS.APPLE_PAY_TAX_REQUEST_EVENT:
        paymentMethodsEventBus.emit('applePayTaxValueRequest', data, { source: 'asyncResponse' });
        break;

      case SECURE_PAY_EVENTS.APPLE_PAY_OPERATION_ABORTED:
        paymentMethodsEventBus.emit('applePayAborted', { source: 'asyncResponse' });
        break;

      default:
    }
  }

  private startTokenTimeout() {
    this.clearTokenTimeout();

    this.tokenTimeoutId = setTimeout(() => {
      this.pageStore.setState({
        applePayTokenExpired: true,
        applePayConnected: false,
        applePaySecurePayIframeBlockedMessage: false,
        applePayCreditCardData: null,
        applePayToken: null,
        applePayTotal: null,
      });
    }, APPLE_PAY_TOKEN_TIMEOUT_TIME) as unknown as number;
  }

  private clearTokenTimeout() {
    if (this.tokenTimeoutId) {
      clearTimeout(this.tokenTimeoutId);
      this.tokenTimeoutId = null;
    }
  }

  /**
   * "1027" => "10/2027"
   * "2025-12-01T05:00:00.000+00:00" => "12/2025" (ISO date string)
   */
  private transformCreditCardExpirationDate(expirationDate?: string) {
    if (!expirationDate) return '';

    // Handle ISO date strings (e.g., "2025-12-01T05:00:00.000+00:00")
    if (expirationDate.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(expirationDate)) {
      try {
        const date = new Date(expirationDate);

        if (!Number.isNaN(date.getTime())) {
          // Use UTC methods to avoid timezone conversion issues
          const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // getUTCMonth() returns 0-11

          const year = date.getUTCFullYear();

          return `${month}/${year}`;
        }
      } catch {
        // If parsing fails, fall through to other format checks
      }
    }

    if (expirationDate && !expirationDate.includes('/')) {
      return `${expirationDate.slice(0, 2)}/20${expirationDate.slice(2, 4)}`;
    }

    return expirationDate;
  }

  private chunkCreditCardNumber(cardNumber: string, cardType: string) {
    // remove spaces
    const cleanNumber = cardNumber.replace(/\s+/g, '');

    if (this.isAmexCreditCard(cardType)) {
      // match AMEX 15-digit format - 4 last digits + 11 "*"" characters
      const trimmedMasked = cardNumber.slice(-4).padStart(15, '*');

      // AMEX format: 4-6-5
      return trimmedMasked.replace(/(.{4})(.{6})(.{5})/, '$1 $2 $3');
    }

    // Default format: 4-4-4-4
    return cleanNumber.replace(/(.{4})/g, '$1 ').trim();
  }

  private isAmexCreditCard(cardType) {
    return [paymentMethodsEnum.AMERICANEXPRESS_ABBREVIATED, paymentMethodsEnum.AMERICANEXPRESS_WORD].includes(cardType);
  }

  private getPaymetLabel() {
    return this.t('application-layer.payment.apple-pay-payment-label');
  }
}
